import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, CheckCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { register as registerAction } from '../store/actions/authActions';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';

const AuthWrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: ${({ theme }) => theme.colors.muted};
`;

const AuthCard = styled(motion.div)`
  width: 100%;
  max-width: 24rem;
  padding: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const LogoLink = styled(Link)`
  display: inline-block;
  font-size: 1.5rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 0.25rem 0;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 1.5rem 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  box-sizing: border-box;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitBtn = styled.button`
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  &:hover {
    opacity: 0.9;
  }
`;

const Divider = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 1.25rem 0 0 0;
  text-align: center;
`;

const AuthLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  display: block;
  text-align: center;
  margin-top: 0.5rem;
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMsg = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

function Register() {
  const { prependPath } = useLocale();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(registerAction({ name, email, password }));
    if (result?.success) {
      setSubmitted(true);
      setTimeout(() => navigate(prependPath('/')), 1200);
    }
  };

  return (
    <AuthWrap>
      <SeoHead title="Register | CiteMatch" description="Create a CiteMatch account." noindex />
      <AuthCard initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <LogoLink to={prependPath('/')}>CiteMatch</LogoLink>
        <Title>Create account</Title>
        <Subtitle>Sign up to save favorites and contribute reviews.</Subtitle>

        {submitted || isAuthenticated ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CheckCircle size={40} style={{ color: 'hsl(142, 71%, 45%)' }} />
            </div>
            <p style={{ margin: '1rem 0 0 0', fontWeight: 600, textAlign: 'center' }}>Account created!</p>
            <SuccessMsg style={{ textAlign: 'center' }}>Redirecting...</SuccessMsg>
          </>
        ) : (
          <>
            <Form onSubmit={handleSubmit}>
              {error && <p style={{ color: 'var(--crimson)', margin: '0 0 0.25rem 0' }}>{error}</p>}
              <div>
                <Label>Name</Label>
                <Input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" minLength={6} />
              </div>
              <SubmitBtn type="submit" disabled={loading}>
                <UserPlus size={18} /> Sign up
              </SubmitBtn>
            </Form>
            <Divider>Already have an account?</Divider>
            <AuthLink to={prependPath('/login')}>Log in</AuthLink>
          </>
        )}
      </AuthCard>
    </AuthWrap>
  );
}

export default Register;
